import { useMutation, useQueryClient, UseMutationOptions, QueryKey } from '@tanstack/react-query';
import { toast } from 'react-toastify';

interface UseApiMutationOptions<TData, TError, TVariables>
  extends UseMutationOptions<TData, TError, TVariables> {
    successMessage?: string;
    errorMessage?: string;
    invalidateQueryKeys?: QueryKey[];
}

/**
 * A reusable wrapper around TanStack's useMutation.
 * Handles toast notifications, cache invalidation, and optimistic updates.
 * @param mutationFn The async function to perform the mutation.
 * @param options Configuration for the mutation.
 */
export const useApiMutation = <
    TData = unknown,
    TError = Error,
    TVariables = void
>(
    mutationFn: (variables: TVariables) => Promise<TData>,
    options: UseApiMutationOptions<TData, TError, TVariables>
) => {
    const queryClient = useQueryClient();
    return useMutation<TData, TError, TVariables>({
        mutationFn,
        onSuccess: (data, variables, context) => {
            // Show success toast
            if (options.successMessage) {
                toast.success(options.successMessage);
            }
            // Run user-provided onSuccess callback
            options.onSuccess?.(data, variables, context);
        },
        onError: (error, variables, context) => {
            // Show error toast
            if (options.errorMessage) {
                toast.error(options.errorMessage);
            }
            // Run user-provided onError callback
            options.onError?.(error, variables, context);
        },
        onSettled: (data, error, variables, context) => {
            // Invalidate specified queries on settled (always, for refresh consistency)
            if (options.invalidateQueryKeys) {
                options.invalidateQueryKeys.forEach(key => {
                    queryClient.invalidateQueries({ queryKey: key });
                });
            }
            // Run user-provided onSettled callback
            options.onSettled?.(data, error, variables, context);
        },
        // Pass through any other user-provided options
        ...options,
    });
};